import React, {useEffect, useRef} from 'react';
import {StyleSheet, View} from 'react-native';

import type {RemoteStream} from '../lib/webrtc';
import {radii, useTheme} from '@/shared/ui/theme';

/**
 * The rectangle a live track is painted into. Browser half — see `VideoSurface.tsx`
 * for why this is a file split rather than a Platform branch.
 *
 * `React.createElement('video')` is a real `<video>` element here and not a mistake:
 * the web build renders through react-dom (react-native-web is a component library on
 * top of it, not a second renderer), so an intrinsic DOM element is exactly what it
 * looks like. That is the only way to reach `srcObject`, which is how a MediaStream
 * has been attached since `createObjectURL(stream)` was removed from browsers.
 *
 * `muted` is not a preference, it is what makes the picture appear: an autoplaying
 * video with sound is blocked outright by every browser's autoplay policy, and the
 * failure is a still frame with no error. Audio, if this ever carries any, needs a
 * user gesture and its own control.
 */
const styles = StyleSheet.create({
  surface: {flex: 1, borderRadius: radii.lg, overflow: 'hidden'},
  square: {borderRadius: 0, backgroundColor: 'transparent'},
});

const videoStyle = {width: '100%', height: '100%', objectFit: 'contain' as const};

export function VideoSurface({
  stream,
  rounded = true,
}: {
  stream: RemoteStream;
  /** Окно раскладки: встык с соседями, без углов и на чёрном фоне самого окна. */
  rounded?: boolean;
}) {
  const p = useTheme();
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // The cast is the price of the file split: `tsc` has no notion of webpack's
    // `.web.tsx` resolution, so it types `RemoteStream` from the NATIVE half of
    // `webrtc.ts` — react-native-webrtc's MediaStream, structurally similar to the
    // browser's but not the same declaration. At runtime this is a real browser
    // MediaStream, because only the browser bundle ever loads this file.
    el.srcObject = stream as unknown as MediaProvider;
    // Chrome rejects this promise when the tab is backgrounded at the moment the
    // track arrives. It is not a failure — the element plays when the tab is looked
    // at — so it must not surface as a broken stream.
    void el.play?.().catch(() => {});
    return () => {
      el.srcObject = null;
    };
  }, [stream]);

  return (
    <View
      style={[styles.surface, {backgroundColor: p.foreground}, !rounded && styles.square]}>
      {React.createElement('video', {
        ref,
        autoPlay: true,
        playsInline: true,
        muted: true,
        style: videoStyle,
      })}
    </View>
  );
}
